/* eslint-disable operator-linebreak */
/* eslint-disable max-lines-per-function */
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles} from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import CloudDownload from '@material-ui/icons/CloudDownload';
import CloudUpload from '@material-ui/icons/CloudUpload';
import Visibility from '@material-ui/icons/Visibility';
import Alert from '@material-ui/lab/Alert';
import {PropTypes} from 'prop-types';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useParams} from 'react-router-dom';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {
  addSignedDocument,
  getApplicationByUuid
} from '../script/application-service';
import {addNotifications} from '../store/appActions';
import DocumentValidation from './DocumentValidation';
import SwisscomSignDialog from './SwisscomSignDialog';

const useStyles = makeStyles((theme) => ({
  headline: {paddingTop: '3%', paddingBottom: '1%', color: 'white'},
  alert: {marginTop: '3%'},
  list: {backgroundColor: theme.palette.background.default},
  dialogContent: {paddingBottom: 0},
  errorBox: {marginTop: 15},
  error: {fontColor: 'red'},
  errorMsg: {marginTop: theme.spacing(5)},
  root: {flexGrow: 1, color: 'black'},
  gridContainer: {margin: theme.spacing(2, 1)},
  container: {display: 'flex', flexWrap: 'wrap'},
  itemButton: {marginTop: 20},
  paper: {padding: theme.spacing(2, 2), width: '100%'},
  button: {margin: '1em 0 1.5em 0', align: 'left'},
  buttonRow: {margin: '1em 0 0 0', align: 'left'},
  mobileNumberField: {
    marginRight: theme.spacing(1),
    marginLeft: -6,
    width: '100%'
  }
}));

/**
 * Notary Registration view for submitting signed document.
 *
 * @param {P} props - The property data.
 * @return {*} - The rendered string.
 */
function NotaryRegistration(props) {
  const classes = useStyles();
  const addUploadNotifications = (notifications) =>
    props.dispatch(addNotifications(notifications));
  const {uuid} = useParams();

  const [showRegistration, setShowRegistration] = useState(false);
  const [showInvalidMsg, setShowInvalidMsg] = useState(false);
  const [showUpdatedMsg, setShowUpdatedMsg] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');

  const [applicationId, setApplicationId] = useState();
  const [mobileNumber, setMobileNumber] = useState('');

  const [openSwisscom, setOpenSwisscom] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);

  const [signed, setSigned] = useState(false);
  const [file, setFile] = useState();
  const [fileError, setFileError] = useState(false);
  const [pending, setPending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [validationResult, setValidationResult] = useState();

  const handleOpenSwisscom = () => {
    setOpenSwisscom(true);
  };

  const handleOpenUpload = () => {
    setFile();
    setFileError();
    setValidationResult();
    setOpenUpload(true);
  };

  const handleCloseSwisscom = () => {
    setOpenSwisscom(false);
  };

  const handleCloseUpload = () => {
    setOpenUpload(false);
  };

  const handleSwisscomSuccess = () => {
    setOpenSwisscom(false);
    setSigned(true);
    setShowSuccess(true);
  };

  /**
   * Handle file changes.
   *
   * @param {Event} event - The file change event.
   */
  const handleFileChange = (event: Event) => {
    const inputFiles = event.target.files;

    setFileError();
    setValidationResult();

    if (inputFiles.length === 1) {
      const file = inputFiles[0];
      if (isValidFile(file)) {
        setFile(file);
      }
    } else {
      setFile(null);
    }
  };

  /**
   * Handle file upload submit.
   *
   * @param {File} file - The uploaded file.
   */
  const handleFileSubmit = async (file: File) => {
    setPending(true);
    if (!pending) {
      addSignedDocument(file, uuid).then((response) => {
        setPending(false);
        if (response.error) {
          setFileError(response.error);
        }

        if (response.created) {
          addUploadNotifications([
            new Notification(
              locale.signatureTemplateUploadCompleted,
              'success',
              6000
            )
          ]);
          setShowSuccess(true);
          setOpenUpload(false);
          setValidationResult(null);
        } else {
          setValidationResult(response);
        }
      });
      setFile(file);
    }
  };

  const renderUploadValidationResult = () => {
    return (
      <>
        {validationResult && validationResult?.info && (
          <Grid xs={12} item>
            <Typography
              id='upload-message'
              variant='body1'
              style={{color: 'red', marginTop: 25}}
            >
              {locale.documentValidationFailed}
            </Typography>
            <DocumentValidation
              profileMode={true}
              validationResult={validationResult}
            />
          </Grid>
        )}
      </>
    );
  };

  const renderUploadDialog = () => {
    return (
      <Dialog
        disableBackdropClick
        aria-labelledby='Signature declaration upload'
        onClose={handleCloseUpload}
        open={openUpload}
      >
        <DialogTitle
          id='signature-declaration-upload-dialog-title'
          onClose={handleCloseUpload}
        >
          {locale.registrationUploadDialogTitle}
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <Typography>{locale.registrationUploadDialogDescription}</Typography>
          <Grid container spacing={0} className={classes.gridContainer}>
            <Grid xs={6} item>
              {renderSignatureDeclarationButton(false, false)}
            </Grid>
            <Grid xs={6} item>
              <input
                id='file-input'
                required
                accept='.pdf'
                type='file'
                name='file'
                style={{marginTop: '0.5em'}}
                onChange={(event) => {
                  handleFileChange(event);
                }}
              />
            </Grid>
            {renderUploadValidationResult()}
            {fileError && (
              <Alert severity='error' style={{marginTop: 15, width: '90%'}}>
                {fileError}
              </Alert>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpload} variant='outlined'>
            {locale.cancelButton}
          </Button>
          <Button
            disableElevation={pending}
            disableRipple={pending}
            disabled={!file}
            variant='contained'
            color='primary'
            onClick={() => {
              handleFileSubmit(file);
            }}
            endIcon={
              pending ? (
                <CircularProgress color='secondary' size={23} />
              ) : (
                <CloudUpload />
              )
            }
          >
            {locale.uploadButton}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  /**
   * Validate a selected File.
   *
   * @param {File} file - A file to validate.
   * @return {Boolean} - Return true if the file is a pdf, else return false.
   */
  const isValidFile = (file: File) => {
    const index = file.name.lastIndexOf('.');
    if (index === -1) {
      return false;
    }
    const fileExt = file.name.substring(index + 1);
    return fileExt.toLowerCase() === 'pdf';
  };

  const renderSignatureDeclarationButton = (
    signedTemplate: boolean,
    styling: boolean
  ) => {
    const url = signedTemplate
      ? '/registration/' + applicationId + '/signed-document'
      : '/registration/' + uuid + '/signature-declaration';
    const tooltip = signedTemplate
      ? locale.registrationUploadDialogActionDownloadTooltip
      : locale.registrationButtonSignatureTemplateTooltip;
    return (
      <Tooltip placement={signedTemplate ? 'bottom' : 'right'} title={tooltip}>
        <Button
          variant='contained'
          color='primary'
          className={styling ? classes.button : ''}
          onClick={() => window.open(url, '_blank')}
          endIcon={signedTemplate ? <CloudDownload /> : <Visibility />}
        >
          {signedTemplate
            ? locale.registrationUploadDialogActionDownload
            : locale.registrationButtonSignatureTemplate}
        </Button>
      </Tooltip>
    );
  };

  const renderSignButtonsRow = () => {
    return (
      <>
        <Grid xs={4} item>
          <SwisscomSignDialog
            applicationId={applicationId}
            open={openSwisscom}
            buttonStyle={classes.buttonRow}
            handleOpen={handleOpenSwisscom}
            handleClose={handleCloseSwisscom}
            handleSuccess={handleSwisscomSuccess}
            mobileNumber={mobileNumber}
            registration={true}
          />
        </Grid>
        <Grid xs={8} item>
          <Tooltip
            placement='top'
            title={locale.registrationButtonSignOthersTooltip}
          >
            <Button
              variant='outlined'
              color='primary'
              className={classes.buttonRow}
              style={{marginLeft: '-1.5em'}}
              onClick={handleOpenUpload}
              endIcon={<CloudDownload />}
            >
              {locale.registrationButtonSignOthers}
            </Button>
          </Tooltip>
        </Grid>
      </>
    );
  };

  const renderRegistrationForm = () => {
    return (
      <form id='document-form' className={classes.container} autoComplete='on'>
        <Paper className={classes.paper}>
          {!showSuccess && (
            <Grid container spacing={0} className={classes.gridContainer}>
              <Grid xs={12} item>
                <Typography variant='body1' gutterBottom>
                  {locale.completeRegistrationDescription}
                </Typography>
              </Grid>
              <Grid xs={12} item>
                {renderSignatureDeclarationButton(false, true)}
              </Grid>
              <Grid xs={12} item>
                <Typography variant='body1' gutterBottom>
                  {locale.registrationSignDescription}
                </Typography>
              </Grid>
              {renderSignButtonsRow()}
            </Grid>
          )}
          {showSuccess && (
            <Grid container spacing={0} className={classes.gridContainer}>
              <Grid xs={12} item>
                <Typography variant='body1' component='span'>
                  <Alert severity='success' style={{width: '95%'}}>
                    {signed
                      ? locale.registrationSwisscomDialogStatusSignSuccess
                      : locale.signatureUploadSuccessMessage}
                  </Alert>
                </Typography>
              </Grid>
              {signed && (
                <Grid xs={12} item className={classes.itemButton}>
                  {renderSignatureDeclarationButton(true, false)}
                </Grid>
              )}
            </Grid>
          )}
        </Paper>
      </form>
    );
  };

  const renderRegistrationContent = () => {
    return (
      <>
        <Typography variant='h5' gutterBottom className={classes.headline}>
          {locale.completeRegistration}
        </Typography>
        {renderRegistrationForm()}
        {renderUploadDialog()}
      </>
    );
  };

  const handleShowContent = (
    registration: boolean,
    updateMsg: boolean,
    invalidMsg: boolean
  ) => {
    setShowRegistration(registration);
    setShowUpdatedMsg(updateMsg);
    setShowInvalidMsg(invalidMsg);
  };

  useEffect(() => {
    getApplicationByUuid(uuid).then((response) => {
      if (!response || !response.status || response.error) {
        handleShowContent(false, false, true);
      } else if (
        response.status === 'UPDATED' ||
        response.status === 'APPROVED'
      ) {
        setResponseStatus(response.status);

        handleShowContent(false, true, false);
      } else {
        handleShowContent(true, false, false);
        setApplicationId(response.id);
        setMobileNumber(response.mobileNumber ?? '');
      }
    });
  }, []);

  return (
    <div id='root' className={classes.root}>
      {showRegistration && renderRegistrationContent()}
      {showInvalidMsg && (
        <Alert severity='error' className={classes.alert}>
          {locale.registrationIdInvalid}
        </Alert>
      )}
      {showUpdatedMsg && (
        <Alert severity='success' className={classes.alert}>
          {responseStatus === 'UPDATED'
            ? locale.registrationUpdatedMsg
            : locale.registrationCompletedMsg}
        </Alert>
      )}
    </div>
  );
}

/**
 * Map the properties.
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    dispatch: dispatch
  };
}

NotaryRegistration.propTypes = {
  dispatch: PropTypes.func.isRequired
};

export default connect(null, mapDispatchToProps)(NotaryRegistration);
